import { ApiError } from '../../api/client';
import type { CommerceCommand, CommerceResult } from '../../api/commerce';
import { uiText } from '../../i18n';

export interface SubscriptionIntent {
  owner: number;
  key: string;
  command: CommerceCommand;
  unknown?: boolean;
  cancelRequested?: boolean;
  cancelKey?: string;
  payment?: CommerceResult & { invoice_id: string };
}
const intentKey = (owner: number) => `steos:subscription:intent:${owner}`;
export function readSubscriptionIntent(owner: number): SubscriptionIntent | null {
  const raw = sessionStorage.getItem(intentKey(owner));
  if (!raw) return null;
  const value = JSON.parse(raw) as SubscriptionIntent;
  if (
    (value.cancelRequested || value.cancelKey) &&
    (!value.payment ||
      value.cancelRequested !== true ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.cancelKey ?? ''))
  )
    throw new Error('subscription_intent_invalid');
  if (value.payment && (typeof value.payment.invoice_id !== 'string' || !value.payment.invoice_id || value.payment.invoice_id.length > 128))
    throw new Error('subscription_intent_invalid');
  if (value.command && 'via' in value.command && !['dev_free', 'stars', 'card'].includes(String(value.command.via)))
    throw new Error('subscription_intent_invalid');
  if (
    value.owner !== owner ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.key) ||
    !value.command ||
    !['plan', 'pack', 'cancel', 'resume', 'downgrade', 'promo', 'gift', 'gift_activate', 'gift_share'].includes(value.command.kind)
  )
    throw new Error('subscription_intent_invalid');
  if ('id' in value.command && (!value.command.id || value.command.id.length > 128)) throw new Error('subscription_intent_invalid');
  if ('months' in value.command && ![1, 3, 12].includes(value.command.months)) throw new Error('subscription_intent_invalid');
  if (['plan', 'pack', 'downgrade'].includes(value.command.kind) && (!('id' in value.command) || typeof value.command.id !== 'string'))
    throw new Error('subscription_intent_invalid');
  if (['plan', 'downgrade'].includes(value.command.kind) && !('months' in value.command)) throw new Error('subscription_intent_invalid');
  if (
    ['plan', 'pack'].includes(value.command.kind) &&
    (!('revision' in value.command) ||
      typeof value.command.revision !== 'number' ||
      !Number.isSafeInteger(value.command.revision) ||
      value.command.revision < 1)
  )
    throw new Error('subscription_intent_invalid');
  if (
    value.command.kind === 'plan' &&
    (typeof value.command.priceVersion !== 'string' || !value.command.priceVersion || value.command.priceVersion.length > 128)
  )
    throw new Error('subscription_intent_invalid');
  if ('code' in value.command && (typeof value.command.code !== 'string' || !value.command.code || value.command.code.length > 128))
    throw new Error('subscription_intent_invalid');
  if (
    value.command.kind === 'gift' &&
    (typeof value.command.recipient !== 'string' ||
      typeof value.command.wish !== 'string' ||
      value.command.wish.length > 200 ||
      typeof value.command.signed !== 'boolean' ||
      !Number.isSafeInteger(value.command.revision))
  )
    throw new Error('subscription_intent_invalid');
  return value;
}
export function saveSubscriptionIntent(intent: SubscriptionIntent) {
  sessionStorage.setItem(intentKey(intent.owner), JSON.stringify(intent));
}
export function clearSubscriptionIntent(owner: number) {
  sessionStorage.removeItem(intentKey(owner));
}

export function subscriptionError(error: unknown) {
  if (!(error instanceof ApiError)) return uiText('Не удалось связаться с сервером. Проверьте соединение и повторите попытку.');
  if (error.code === 'gift_recipient_invalid') {
    const reasons: Record<string, string> = {
      chars: 'Латиница, цифры и «_»',
      first: 'Ник начинается с буквы',
      short: 'Не короче 5 знаков',
      long: 'Не длиннее 32 знаков',
      tail: 'Ник не кончается на _',
      self: 'Это ваш ник',
      not_found: 'Получатель не найден среди пользователей STEOS. Проверьте ник или попросите друга запустить бота.',
    };
    if (typeof error.details?.reason === 'string' && reasons[error.details.reason]) return uiText(reasons[error.details.reason]);
  }
  const errors: Record<string, string> = {
    price_confirmation_required: 'Условия оплаты изменились. Обновите данные.',
    gift_share_forbidden: 'Эту открытку может переслать только отправитель подарка.',
    material_bot_unavailable: 'Откройте чат с ботом и попробуйте ещё раз.',
    gift_recipient_unavailable: 'Доставка получателю не подтверждена. Перешлите открытку самостоятельно.',
    product_not_found: 'Предложение не найдено. Обновите данные.',
    promo_not_found: 'Такого промокода нет',
    promo_expired: 'Срок действия промокода закончился',
    promo_not_started: 'Этот промокод пока не действует',
    promo_used: 'Вы уже активировали этот промокод',
    promo_limit: 'Промокод больше не действует',
    promo_own: 'Это ваш промокод',
    promo_after_purchase: 'Партнёрские промокоды работают только до первой покупки',
    promo_paused: 'Промокод партнёра временно недоступен',
    partner_already_bound: 'Вы уже привязаны к партнёру',
    gift_recipient_confirmation_required: 'Получатель изменился. Отмените прежний неоплаченный счёт и проверьте ник перед новой покупкой.',
    gift_wish_invalid: 'Пожелание должно быть не длиннее 200 знаков, без управляющих символов.',
    gift_recipient_invalid: 'Проверьте ник: получатель должен запускать STEOS, нельзя дарить себе',
    gift_recipient_busy: 'Получатель сейчас занят. Повторите позже',
    gift_not_found: 'Подарок не найден',
    gift_not_yours: 'Этот подарок предназначен другому пользователю',
    gift_already_activated: 'Подарок уже получен',
    subscription_already_active: 'Подписка уже активна. Обновите данные и выберите доступное действие.',
    subscription_not_found: 'Подписка уже закончилась. Обновите данные.',
    commerce_revision_conflict: 'Условия тарифа изменились. Обновите данные перед подтверждением.',
    price_changed: 'Условия тарифа изменились. Обновите данные перед подтверждением.',
    commerce_payment_provider_unconfigured: 'Покупка сейчас недоступна. Реальные платежи не включены.',
    subscription_schedule_invalid: 'Не удалось подтвердить условия продления. Обновите тарифы.',
    subscription_schedule_changed: 'Подписка изменилась. Обновите данные перед новым продлением.',
    subscription_schedule_exists: 'Продление уже запланировано. Его можно отменить в управлении подпиской.',
    subscription_schedule_canceled: 'Запланированное продление отменено. Обновите данные.',
    subscription_schedule_not_due: 'Счёт будет доступен в конце текущего срока.',
    gift_period_active: 'Месячная подписка будет доступна после окончания подарка. Сейчас можно продлить на 3 или 12 месяцев.',
    subscription_control_pending: 'Предыдущее изменение подписки ещё проверяется. Повторите его проверку.',
    subscription_provider_confirmation_required: 'Изменение подписки ещё не подтверждено Telegram.',
    subscription_revision_conflict: 'Подписка изменилась. Обновите данные и повторите проверку.',
    referral_discount_reserved: 'Скидка уже закреплена за предыдущим счётом. Проверьте его оплату.',
    subscription_resume_unavailable: 'Для этой подписки продление пока недоступно.',
    subscription_downgrade_invalid: 'Этот переход между тарифами недоступен.',
    subscription_required: 'Пакеты Circle доступны с активной подпиской.',
    product_unavailable: 'Этот тариф больше недоступен. Обновите список.',
    idempotency_conflict: 'Запрос не совпадает с сохранённой операцией. Не повторяйте покупку; обратитесь в поддержку.',
  };
  return uiText(
    errors[error.code] ??
      (error.status === 429
        ? 'Слишком много запросов. Подождите и повторите проверку.'
        : 'Подписка временно недоступна. Попробуйте обновить данные.'),
  );
}
